import React, { useState, useEffect, useRef } from 'react';
import { useChatSearch } from '../../../hooks/useChatSearch';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '../icons/search.icon';
import '.././../../scss/search-bar.style.scss';


interface ChatSearchBarProps {
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const ChatSearchBar: React.FC<ChatSearchBarProps> = ({
  placeholder = 'Search',
  className = '',
  disabled = false,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const language = new URLSearchParams(window.location.search).get('lang') || 'en-us';
  const navigate = useNavigate();
  const {
    chatData,
    loading,
    fetchChatSearch,
    showDropdown,
    setDropdown
  } = useChatSearch();

  useEffect(() => {
    if (searchValue.trim().length === 0) {
      setDropdown(false);
    }
  }, [searchValue, setDropdown]);


  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setDropdown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    if (e.target.value.trim().length > 0) {
      setDropdown(true);
      fetchChatSearch({ query: e.target.value });
    } else {
      setDropdown(false);
    }
  };

  const handleSelect = (isCustomer: boolean) => {
    if (isCustomer) {
    navigate(`/v2/my-clients?lang=${language}&customerSearch=` + encodeURIComponent(searchValue));
    }
    else {
      navigate(`/v2/manage-invoice?lang=${language}&invoiceSearch=` + decodeURIComponent(searchValue.trim()));
    }
    setDropdown(false);
    setSearchValue('');
  };

  return (
    <div className={`search-bar ${className}`} ref={dropdownRef}>
      <div className="search-bar__input-container">
        <SearchIcon className="search-bar__icon" />
        <input
          type="text"
          value={searchValue}
          onChange={(e) => handleInputChange(e)}
          placeholder={placeholder}
          disabled={disabled}
          className="search-bar__input"
          aria-label="Search input"
        />
      </div>
      {showDropdown && (
        <div className="search-bar__dropdown">
          {loading ? (
            <div className="search-bar__dropdown-item search-bar__dropdown-item--muted">Loading...</div>
          ) : chatData && Array.isArray(chatData.response) ? (
            chatData.response.length === 0 ? (
              <div className="search-bar__dropdown-item search-bar__dropdown-item--muted">No results found</div>
            ) : (
              chatData.response.map((item: any, idx: number) => (
                <div
                  key={item.name + idx}
                  className="search-bar__dropdown-item"
                  onClick={() => handleSelect(item.isCustomer)}
                >
                  <span className="search-bar__dropdown-item-name">{item.name}</span>
                  <span className="search-bar__dropdown-item-desc">{item.isCustomer ? 'Customer' : 'Invoice'}</span>
                </div>
              ))
            )
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ChatSearchBar;
